import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ArrowLeft, Calendar, Users, MessageSquare, Sparkles } from "lucide-react";
import { API, GOLD, RED, fmtDay, clipSrc, ClipThumb, ClipLightbox, ShareClipButton, CoachNote } from "../components/recaps/recapKit";

const Eyebrow = ({ children }) => <div className="font-mono text-[10px] font-bold uppercase tracking-[0.35em] mb-3" style={{ color: GOLD }}>{children}</div>;

const MyClip = ({ clip, token }) => (
  <motion.article data-testid={`recap-my-clip-${clip.id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="rounded-2xl border border-white/10 border-l-4 border-l-[#D4AF37] bg-[#1A1A1A] overflow-hidden sm:flex">
    <div className="bg-black sm:w-[300px] shrink-0 flex justify-center">
      <video data-testid={`recap-my-video-${clip.id}`} src={`${clipSrc(clip, token)}#t=0.1`} controls playsInline preload="metadata" className="w-full max-w-[340px] aspect-[9/16] object-contain bg-black" />
    </div>
    <div className="p-4 sm:p-6 space-y-4 flex-1 min-w-0 flex flex-col">
      {clip.move_label && <div><div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/45">Move</div><div data-testid={`recap-my-move-${clip.id}`} className="font-headings text-3xl uppercase tracking-wide" style={{ color: GOLD }}>{clip.move_label}</div></div>}
      <CoachNote note={clip.note} testid={`recap-my-note-${clip.id}`} />
      {!clip.note && <p className="text-sm text-white/50 flex items-center gap-2"><MessageSquare size={13} /> No note on this one — the clip speaks for itself.</p>}
      <div className="mt-auto pt-2"><ShareClipButton clip={clip} token={token} /></div>
    </div>
  </motion.article>
);

const MovesAttempted = ({ moves }) => moves.length > 0 && (
  <section data-testid="recap-moves" className="mt-10">
    <Eyebrow>Moves attempted</Eyebrow>
    <div className="flex flex-wrap gap-2">
      {moves.map((m) => (
        <span key={m.label} data-testid={`recap-move-${m.label.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-bold uppercase tracking-wider"
          style={{ borderColor: m.mine ? `${GOLD}88` : "rgba(255,255,255,0.12)", color: m.mine ? GOLD : "rgba(255,255,255,0.75)", background: m.mine ? `${GOLD}12` : "rgba(255,255,255,0.03)" }}>
          {m.label}<span className="font-mono text-[10px] text-white/45">×{m.count}</span>{m.mine && <span className="text-[9px] text-white/60">· you</span>}
        </span>
      ))}
    </div>
  </section>
);

const Teammates = ({ clips, token, onOpen }) => (
  <section data-testid="recap-teammates" className="mt-10">
    <Eyebrow>Your Teammates</Eyebrow>
    {clips.length === 0 ? <p data-testid="recap-teammates-empty" className="text-sm text-white/50">No teammate clips have been shared from this session yet.</p> : (
      <div className="-mx-5 px-5 sm:mx-0 sm:px-0 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3" style={{ WebkitOverflowScrolling: "touch" }}>
        {clips.map((c) => (
          <div key={c.id} className="snap-start shrink-0 w-36 sm:w-44">
            <ClipThumb clip={c} token={token} testid={`recap-teammate-${c.id}`} onClick={() => onOpen(c)} className="w-full aspect-[9/16]" />
            <div className="mt-2 font-headings uppercase tracking-wide text-sm truncate">{c.first_name}</div>
            {c.move_label && <div className="font-mono text-[10px] uppercase tracking-wider truncate" style={{ color: GOLD }}>{c.move_label}</div>}
          </div>
        ))}
      </div>
    )}
  </section>
);

const fmtN = (n) => (Number.isInteger(n) ? n : n.toFixed(1));

const HowYouCompare = ({ rows }) => rows?.length > 0 && (
  <section data-testid="recap-compare" className="mt-12">
    <Eyebrow>How You Compare</Eyebrow>
    <h2 className="font-headings text-2xl uppercase tracking-wide mb-4">Your Session vs. The Group</h2>
    <div className="rounded-2xl border border-white/10 bg-[#1A1A1A] overflow-hidden">
      <div className="grid grid-cols-[1.4fr_repeat(3,minmax(0,1fr))] px-4 py-2.5 border-b border-white/10 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-white/45">
        <span />
        <span className="text-center" style={{ color: RED }}>You</span>
        <span className="text-center">Session Avg</span>
        <span className="text-center" style={{ color: GOLD }}>Session High</span>
      </div>
      {rows.map((r, i) => (
        <motion.div key={r.key} data-testid={`recap-compare-${r.key}`} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.08 }}
          className="grid grid-cols-[1.4fr_repeat(3,minmax(0,1fr))] items-center px-4 py-3.5 border-b border-white/5 last:border-b-0">
          <span className="text-sm text-white/80">{r.label}</span>
          <span data-testid={`recap-compare-${r.key}-you`} className="text-center font-cover text-3xl leading-none" style={{ color: r.you >= r.high && r.high > 0 ? GOLD : "#fff" }}>{fmtN(r.you)}</span>
          <span data-testid={`recap-compare-${r.key}-avg`} className="text-center font-cover text-2xl leading-none text-white/60">{fmtN(r.avg)}</span>
          <span data-testid={`recap-compare-${r.key}-high`} className="text-center font-cover text-2xl leading-none" style={{ color: GOLD }}>{fmtN(r.high)}</span>
        </motion.div>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-white/35">Group numbers include everyone tagged in this session. No names, just the totals.</p>
  </section>
);

export default function RecapView() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const { token } = usePlayerAuth();
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => axios.get(`${API}/me/recaps/${sid}`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setR(data)).catch(() => setR({ error: true })), [sid, token]);
  useEffect(() => { load(); }, [load]);
  const processing = r?.clips?.some((c) => c.share_status === "processing");
  useEffect(() => { if (!processing) return undefined; const t = setInterval(load, 6000); return () => clearInterval(t); }, [processing, load]);
  const close = useCallback(() => setOpen(null), []);
  if (!r) return <div className="min-h-screen bg-[#111111] flex items-center justify-center text-white/40" data-testid="recap-loading">Loading your recap…</div>;
  return (
    <div className="min-h-screen bg-[#111111] text-white grain" data-testid="recap-view">
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-24">
        <button data-testid="recap-back" onClick={() => navigate("/recaps")} className="inline-flex items-center gap-1.5 min-h-[44px] text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors"><ArrowLeft size={14} /> All recaps</button>
        {r.error ? <p data-testid="recap-error" className="mt-10 text-white/60">This recap isn't available.</p> : (
          <>
            <header className="mt-4 mb-6">
              <Eyebrow><Sparkles size={11} className="inline -mt-0.5 mr-1" />Session Recap</Eyebrow>
              <h1 data-testid="recap-title" className="font-cover text-4xl sm:text-5xl uppercase leading-[0.95]">{r.name}</h1>
              <div className="flex flex-wrap gap-4 mt-3 font-mono text-[11px] uppercase tracking-wider text-white/50">
                <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {fmtDay(r.date)}</span>
                <span data-testid="recap-player-count" className="inline-flex items-center gap-1.5"><Users size={12} /> {r.player_count} player{r.player_count === 1 ? "" : "s"}</span>
              </div>
            </header>
            {r.clips.length === 0 ? <p data-testid="recap-no-clip" className="text-sm text-white/55 rounded-2xl border border-white/10 p-5">Coach Barker didn't post a clip of you from this one — your teammates' highlights are below.</p>
              : <div className="space-y-5">{r.clips.map((c) => <MyClip key={c.id} clip={c} token={token} />)}</div>}
            <MovesAttempted moves={r.moves} />
            <Teammates clips={r.teammates} token={token} onOpen={setOpen} />
            <HowYouCompare rows={r.compare} />
          </>
        )}
      </main>
      {open && <ClipLightbox clip={open} token={token} onClose={close} />}
    </div>
  );
}
